import { createElement, useId } from 'react'
import { cn } from '#app/utils/misc'

/**
 * Factory to create reusable layout components with customizable tag and class name.
 *
 * @param {Object} options - Configuration for the layout component.
 * @param {keyof JSX.IntrinsicElements} options.defaultTagName - Default HTML tag name for the component.
 * @param {string} options.defaultClassName - Default Tailwind CSS classes for the component.
 * @param {string} options.displayName - Display name for the component (useful for debugging).
 *
 * @returns {FC} A functional component with dynamic props.
 */
interface LayoutComponentProps extends React.HTMLAttributes<HTMLElement> {
	tagName?: keyof JSX.IntrinsicElements
}

interface CreateLayoutComponentOptions {
	defaultTagName?: keyof JSX.IntrinsicElements
	defaultClassName?: string
	displayName: string
}

export const createLayoutComponent = ({
	defaultTagName = 'div',
	defaultClassName = '',
	displayName,
}: CreateLayoutComponentOptions): React.FC<LayoutComponentProps> => {
	const Component: React.FC<LayoutComponentProps> = ({
		id,
		children,
		className,
		tagName,
		...props
	}) => {
		const fallbackId = useId()
		const ComponentId = id || `${displayName}-${fallbackId}`
		const TagName = tagName || defaultTagName
		const ComponentProps = {
			id: ComponentId,
			className: cn(defaultClassName, className),
			...props,
		}

		return createElement(TagName, ComponentProps, children)
	}

	Component.displayName = `LayoutComponent(${displayName})`
	return Component
}

/**
 * Example Usage
 *
 * Usage of the generated components with additional attributes:
 *
 * <GridLayout aria-label="Grid Example" data-testid="grid-layout">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </GridLayout>
 *
 * <FlexLayout aria-hidden="true" data-status="active">
 *   <p>Flex Content</p>
 * </FlexLayout>
 *
 * Create specific layout components:
 * const GridLayout = createLayoutComponent({
 *   defaultTagName: 'div',
 *   defaultClassName: 'grid grid-cols-1 gap-4',
 *   displayName: 'GridLayout',
 * });
 */
